'use client';

import { Fragment } from 'react';
import Link from 'next/link';
import styles from './TeamMeetBanner.module.scss';
import { IconArrowRight } from '@tabler/icons-react';
import { gaCta } from '@/src/helpers/gtag';
import { useAOS } from '@/src/lib/hooks/useAOS';

export interface TeamMeetBannerProps {
  badge?: string;
  title?: string;
  highlightedTitle?: string;
  subtitle?: string;
  backgroundImage?: string;
  imageAlt?: string;
  ctaLabel?: string;
  ctaHref?: string;
}

const DEFAULT_BG = 'https://ik.imagekit.io/lindahestia/articles/averiane-pause-cafe-clients.webp';

const renderTitle = (text: string) => {
  const parts = text.split('*');
  return parts.map((part, i) =>
    i % 2 === 1 ? <em key={i}>{part}</em> : <Fragment key={i}>{part}</Fragment>
  );
};

export function TeamMeetBanner({
  badge,
  title = 'Rencontrons-nous',
  highlightedTitle = '*autour d’un café*',
  subtitle,
  backgroundImage = DEFAULT_BG,
  imageAlt,
  ctaLabel,
  ctaHref,
}: TeamMeetBannerProps) {
  useAOS();

  return (
    <section
      className={styles.root}
      style={{ backgroundImage: `url(${backgroundImage})` }}
      aria-label={imageAlt || title}
    >
      <div className={styles.overlay} aria-hidden="true" />
      <div className={styles.inner}>
        {badge && (
          <p className={styles.badge} data-aos="fade-down" data-aos-duration="700">
            {badge}
          </p>
        )}
        <h2 className={styles.title} data-aos="fade-up" data-aos-duration="700">
          {renderTitle(title)}{' '}
          <span className={styles.highlight}>{renderTitle(highlightedTitle)}</span>
        </h2>
        {subtitle && (
          <p
            className={styles.subtitle}
            data-aos="fade-up"
            data-aos-duration="800"
            data-aos-delay="100"
          >
            {subtitle}
          </p>
        )}
        {ctaLabel && ctaHref && (
          <Link
            href={ctaHref}
            className={styles.cta}
            data-aos="fade-up"
            data-aos-duration="700"
            data-aos-delay="200"
            onClick={() => gaCta(ctaLabel, 'team_meet_banner_cta', ctaHref)}
          >
            {ctaLabel}
            <IconArrowRight size={16} aria-hidden="true" />
          </Link>
        )}
      </div>
    </section>
  );
}
